import React, { useCallback, useEffect, useState } from "react";
import {
  Container,
  Row,
  Col,
  Button,
  Form,
  Modal,
  Table,
  Badge,
  Spinner,
  Alert,
  ButtonGroup,
} from "react-bootstrap";
import DatePicker from "react-datepicker";
import "react-datepicker/dist/react-datepicker.css";
import { FaPlus, FaEdit, FaTrash, FaCheck, FaFileAlt, FaDownload, FaTimes } from "react-icons/fa";
import { toast } from "react-hot-toast";
import { api } from "../../../contexts/AuthContext";

const ACCENT = "#60A5FA";

const emptyForm = (direction = "outgoing") => ({
  direction,
  invoice_number: "",
  counterparty: "",
  company: "",
  issue_date: new Date(),
  due_date: null,
  amount: "",
  vat_amount: "0",
  currency: "EUR",
  status: "open",
  description: "",
  notes: "",
});

const toDateKey = (value) => {
  if (!value) return null;
  if (typeof value === "string" && /^\d{4}-\d{2}-\d{2}/.test(value)) {
    return value.slice(0, 10);
  }
  const d = value instanceof Date ? value : new Date(value);
  if (Number.isNaN(d.getTime())) return null;
  const y = d.getFullYear();
  const m = String(d.getMonth() + 1).padStart(2, "0");
  const day = String(d.getDate()).padStart(2, "0");
  return `${y}-${m}-${day}`;
};

const parseDateKey = (key) => {
  if (!key) return null;
  const [y, m, d] = String(key).slice(0, 10).split("-").map(Number);
  if (!y || !m || !d) return null;
  return new Date(y, m - 1, d, 12, 0, 0, 0);
};

const formatDate = (value) => {
  const d = parseDateKey(value);
  if (!d) return "—";
  return d.toLocaleDateString("en-GB");
};

const formatMoney = (amount, currency = "EUR") => {
  const n = parseFloat(amount) || 0;
  return `${n.toLocaleString("en-GB", { minimumFractionDigits: 2, maximumFractionDigits: 2 })} ${currency}`;
};

const statusVariant = (status, overdue) => {
  if (overdue) return "danger";
  if (status === "paid") return "success";
  if (status === "draft") return "secondary";
  if (status === "cancelled") return "dark";
  return "warning";
};

const Invoices = () => {
  const [invoices, setInvoices] = useState([]);
  const [companies, setCompanies] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [directionFilter, setDirectionFilter] = useState("all");
  const [statusFilter, setStatusFilter] = useState("all");
  const [search, setSearch] = useState("");
  const [showModal, setShowModal] = useState(false);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState(emptyForm());
  const [saving, setSaving] = useState(false);
  const [selectedFile, setSelectedFile] = useState(null);
  const [removeFile, setRemoveFile] = useState(false);

  const fetchData = useCallback(async () => {
    try {
      setLoading(true);
      const [invRes, companiesRes] = await Promise.all([
        api.get("/api/invoices/"),
        api.get("/api/companies/"),
      ]);
      setInvoices(invRes.data);
      setCompanies(companiesRes.data);
      setError(null);
    } catch (err) {
      console.error(err);
      setError("Failed to load invoices");
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  const openCreate = (direction = "outgoing") => {
    setEditing(null);
    setForm(emptyForm(direction));
    setSelectedFile(null);
    setRemoveFile(false);
    setShowModal(true);
  };

  const openEdit = (invoice) => {
    setEditing(invoice);
    setSelectedFile(null);
    setRemoveFile(false);
    setForm({
      direction: invoice.direction,
      invoice_number: invoice.invoice_number || "",
      counterparty: invoice.counterparty || "",
      company: invoice.company ? String(invoice.company) : "",
      issue_date: parseDateKey(invoice.issue_date),
      due_date: parseDateKey(invoice.due_date),
      amount: String(invoice.amount ?? ""),
      vat_amount: String(invoice.vat_amount ?? "0"),
      currency: invoice.currency || "EUR",
      status: invoice.status || "open",
      description: invoice.description || "",
      notes: invoice.notes || "",
    });
    setShowModal(true);
  };

  const closeModal = () => {
    setShowModal(false);
    setEditing(null);
    setForm(emptyForm());
    setSelectedFile(null);
    setRemoveFile(false);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!form.counterparty.trim()) {
      toast.error("Counterparty is required");
      return;
    }
    if (!form.issue_date) {
      toast.error("Issue date is required");
      return;
    }
    const amount = parseFloat(form.amount);
    if (isNaN(amount) || amount < 0) {
      toast.error("Enter a valid amount");
      return;
    }

    const payload = {
      direction: form.direction,
      invoice_number: form.invoice_number.trim(),
      counterparty: form.counterparty.trim(),
      company: form.company ? String(parseInt(form.company, 10)) : "",
      issue_date: toDateKey(form.issue_date),
      due_date: form.due_date ? toDateKey(form.due_date) : "",
      amount: amount.toFixed(2),
      vat_amount: (parseFloat(form.vat_amount) || 0).toFixed(2),
      currency: form.currency || "EUR",
      status: form.status,
      description: form.description.trim(),
      notes: form.notes.trim(),
    };

    const formData = new FormData();
    Object.entries(payload).forEach(([key, value]) => {
      if (key === "company" || key === "due_date") {
        if (value === null || value === undefined || value === "") return;
      }
      if (value === null || value === undefined) return;
      formData.append(key, value);
    });
    // Explicitly clear company on edit when emptied
    if (editing && !payload.company) {
      formData.append("company", "");
    }
    if (selectedFile) {
      formData.append("file", selectedFile);
    }
    if (editing && removeFile && !selectedFile) {
      formData.append("remove_file", "true");
    }

    try {
      setSaving(true);
      const config = { headers: { "Content-Type": "multipart/form-data" } };
      if (editing) {
        await api.patch(`/api/invoices/${editing.id}/`, formData, config);
        toast.success("Invoice updated");
      } else {
        await api.post("/api/invoices/", formData, config);
        toast.success("Invoice created");
      }
      closeModal();
      await fetchData();
    } catch (err) {
      console.error(err);
      const data = err.response?.data;
      toast.error(
        data?.detail ||
          (typeof data === "object" ? JSON.stringify(data) : "Failed to save invoice")
      );
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async (invoice) => {
    if (!window.confirm(`Delete invoice ${invoice.invoice_number || "#" + invoice.id}?`)) {
      return;
    }
    try {
      await api.delete(`/api/invoices/${invoice.id}/`);
      setInvoices((prev) => prev.filter((i) => i.id !== invoice.id));
      toast.success("Deleted");
    } catch (err) {
      toast.error("Failed to delete");
    }
  };

  const handleMarkPaid = async (invoice) => {
    try {
      const res = await api.post(`/api/invoices/${invoice.id}/mark_paid/`);
      setInvoices((prev) => prev.map((i) => (i.id === invoice.id ? res.data : i)));
      toast.success("Marked as paid");
    } catch (err) {
      toast.error("Failed to mark paid");
    }
  };

  const filtered = invoices.filter((inv) => {
    if (directionFilter !== "all" && inv.direction !== directionFilter) return false;
    if (statusFilter === "overdue") {
      if (!inv.is_overdue) return false;
    } else if (statusFilter !== "all" && inv.status !== statusFilter) {
      return false;
    }
    if (search.trim()) {
      const q = search.toLowerCase();
      const hay = `${inv.invoice_number} ${inv.counterparty} ${inv.description} ${inv.company_name || ""}`.toLowerCase();
      if (!hay.includes(q)) return false;
    }
    return true;
  });

  const totals = invoices.reduce(
    (acc, inv) => {
      const total = parseFloat(inv.total_amount ?? inv.amount) || 0;
      if (inv.direction === "outgoing") {
        acc.outTotal += total;
        if (inv.status === "paid") acc.outPaid += total;
        else if (inv.status === "open" || inv.status === "draft") acc.outOpen += total;
      } else {
        acc.inTotal += total;
        if (inv.status === "paid") acc.inPaid += total;
        else if (inv.status === "open" || inv.status === "draft") acc.inOpen += total;
      }
      return acc;
    },
    { outTotal: 0, outPaid: 0, outOpen: 0, inTotal: 0, inPaid: 0, inOpen: 0 }
  );

  const chipStyle = (active) => ({
    borderColor: ACCENT,
    color: active ? "#0b1220" : ACCENT,
    backgroundColor: active ? ACCENT : "transparent",
  });

  if (loading) {
    return (
      <Container className="d-flex justify-content-center align-items-center" style={{ minHeight: "50vh" }}>
        <Spinner animation="border" />
      </Container>
    );
  }

  if (error) {
    return (
      <Container className="mt-4">
        <Alert variant="danger">{error}</Alert>
      </Container>
    );
  }

  return (
    <Container fluid className="py-4" style={{ color: "#fff" }}>
      <Row className="mb-3 align-items-center">
        <Col>
          <h2 style={{ color: ACCENT, marginBottom: 4 }}>Invoices</h2>
          <div style={{ color: "#9ca3af", fontSize: 13 }}>
            Anthesys · incoming & outgoing
          </div>
        </Col>
        <Col xs="auto">
          <ButtonGroup>
            <Button variant="primary" onClick={() => openCreate("outgoing")}>
              <FaPlus className="me-2" />
              Outgoing
            </Button>
            <Button variant="outline-primary" style={{ borderColor: ACCENT, color: ACCENT }} onClick={() => openCreate("incoming")}>
              Incoming
            </Button>
          </ButtonGroup>
        </Col>
      </Row>

      <Row className="mb-4 g-3">
        <Col md={6}>
          <div
            style={{
              background: "#1e1e1e",
              border: `1px solid ${ACCENT}55`,
              borderRadius: 8,
              padding: 16,
            }}
          >
            <div style={{ color: ACCENT, fontWeight: 600, marginBottom: 8 }}>Outgoing (issued)</div>
            <div className="d-flex justify-content-between" style={{ fontSize: 14 }}>
              <span style={{ color: "#9ca3af" }}>Open</span>
              <span>{formatMoney(totals.outOpen)}</span>
            </div>
            <div className="d-flex justify-content-between" style={{ fontSize: 14 }}>
              <span style={{ color: "#9ca3af" }}>Paid</span>
              <span style={{ color: "#28a745" }}>{formatMoney(totals.outPaid)}</span>
            </div>
            <div className="d-flex justify-content-between mt-1" style={{ fontWeight: 600 }}>
              <span>Total</span>
              <span>{formatMoney(totals.outTotal)}</span>
            </div>
          </div>
        </Col>
        <Col md={6}>
          <div
            style={{
              background: "#1e1e1e",
              border: "1px solid #fbbf2455",
              borderRadius: 8,
              padding: 16,
            }}
          >
            <div style={{ color: "#fbbf24", fontWeight: 600, marginBottom: 8 }}>Incoming (received)</div>
            <div className="d-flex justify-content-between" style={{ fontSize: 14 }}>
              <span style={{ color: "#9ca3af" }}>Open</span>
              <span>{formatMoney(totals.inOpen)}</span>
            </div>
            <div className="d-flex justify-content-between" style={{ fontSize: 14 }}>
              <span style={{ color: "#9ca3af" }}>Paid</span>
              <span style={{ color: "#28a745" }}>{formatMoney(totals.inPaid)}</span>
            </div>
            <div className="d-flex justify-content-between mt-1" style={{ fontWeight: 600 }}>
              <span>Total</span>
              <span>{formatMoney(totals.inTotal)}</span>
            </div>
          </div>
        </Col>
      </Row>

      <Row className="mb-3 g-2 align-items-center">
        <Col md={4}>
          <Form.Control
            type="search"
            placeholder="Search number, counterparty…"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            className="bg-dark text-light border-secondary"
          />
        </Col>
        <Col md={8} className="d-flex flex-wrap gap-2">
          {[
            { key: "all", label: "All" },
            { key: "outgoing", label: "Outgoing" },
            { key: "incoming", label: "Incoming" },
          ].map((chip) => (
            <Button
              key={chip.key}
              size="sm"
              variant="outline-primary"
              style={chipStyle(directionFilter === chip.key)}
              onClick={() => setDirectionFilter(chip.key)}
            >
              {chip.label}
            </Button>
          ))}
          {[
            { key: "all", label: "Any status" },
            { key: "open", label: "Open" },
            { key: "paid", label: "Paid" },
            { key: "overdue", label: "Overdue" },
            { key: "draft", label: "Draft" },
          ].map((chip) => (
            <Button
              key={`st-${chip.key}`}
              size="sm"
              variant="outline-primary"
              style={chipStyle(statusFilter === chip.key)}
              onClick={() => setStatusFilter(chip.key)}
            >
              {chip.label}
            </Button>
          ))}
        </Col>
      </Row>

      <div
        style={{
          backgroundColor: "#1e1e1e",
          borderRadius: 8,
          padding: 16,
          border: `1px solid ${ACCENT}33`,
        }}
      >
        <Table hover variant="dark" className="align-middle mb-0">
          <thead>
            <tr>
              <th>Number</th>
              <th>Type</th>
              <th>Counterparty</th>
              <th>Issued</th>
              <th>Due</th>
              <th>Total</th>
              <th>File</th>
              <th>Status</th>
              <th className="text-end">Actions</th>
            </tr>
          </thead>
          <tbody>
            {filtered.length === 0 && (
              <tr>
                <td colSpan={9} className="text-center py-4" style={{ color: "#9ca3af" }}>
                  No invoices yet. Add an outgoing or incoming invoice.
                </td>
              </tr>
            )}
            {filtered.map((inv) => (
              <tr key={inv.id}>
                <td>{inv.invoice_number || `—`}</td>
                <td>
                  <Badge bg={inv.direction === "outgoing" ? "primary" : "warning"} text={inv.direction === "incoming" ? "dark" : undefined}>
                    {inv.direction === "outgoing" ? "Outgoing" : "Incoming"}
                  </Badge>
                </td>
                <td>
                  <div>{inv.counterparty}</div>
                  {inv.company_name ? (
                    <small style={{ color: "#9ca3af" }}>{inv.company_name}</small>
                  ) : null}
                </td>
                <td>{formatDate(inv.issue_date)}</td>
                <td>{formatDate(inv.due_date)}</td>
                <td>{formatMoney(inv.total_amount ?? inv.amount, inv.currency)}</td>
                <td>
                  {inv.file_url ? (
                    <a
                      href={inv.file_url}
                      target="_blank"
                      rel="noopener noreferrer"
                      style={{ color: ACCENT, textDecoration: "none" }}
                      title={inv.file_name || "Download"}
                    >
                      <FaDownload className="me-1" />
                      <span style={{ fontSize: 12 }}>Open</span>
                    </a>
                  ) : (
                    <span style={{ color: "#6b7280" }}>—</span>
                  )}
                </td>
                <td>
                  <Badge bg={statusVariant(inv.status, inv.is_overdue)}>
                    {inv.is_overdue ? "Overdue" : inv.status}
                  </Badge>
                </td>
                <td className="text-end">
                  <div className="d-flex justify-content-end gap-1">
                    {inv.status !== "paid" && inv.status !== "cancelled" && (
                      <Button size="sm" variant="outline-success" title="Mark paid" onClick={() => handleMarkPaid(inv)}>
                        <FaCheck />
                      </Button>
                    )}
                    <Button
                      size="sm"
                      variant="outline-primary"
                      style={{ borderColor: ACCENT, color: ACCENT }}
                      onClick={() => openEdit(inv)}
                    >
                      <FaEdit />
                    </Button>
                    <Button size="sm" variant="outline-danger" onClick={() => handleDelete(inv)}>
                      <FaTrash />
                    </Button>
                  </div>
                </td>
              </tr>
            ))}
          </tbody>
        </Table>
      </div>

      <Modal show={showModal} onHide={closeModal} size="lg" contentClassName="bg-dark text-light">
        <Modal.Header closeButton closeVariant="white" style={{ borderBottom: "1px solid #333" }}>
          <Modal.Title>
            {editing ? "Edit invoice" : form.direction === "outgoing" ? "New outgoing invoice" : "New incoming invoice"}
          </Modal.Title>
        </Modal.Header>
        <Form onSubmit={handleSubmit}>
          <Modal.Body>
            <Row>
              <Col md={4}>
                <Form.Group className="mb-3">
                  <Form.Label>Direction</Form.Label>
                  <Form.Select
                    value={form.direction}
                    onChange={(e) => setForm({ ...form, direction: e.target.value })}
                    className="bg-dark text-light border-secondary"
                  >
                    <option value="outgoing">Outgoing (Anthesys issued)</option>
                    <option value="incoming">Incoming (received)</option>
                  </Form.Select>
                </Form.Group>
              </Col>
              <Col md={4}>
                <Form.Group className="mb-3">
                  <Form.Label>Invoice #</Form.Label>
                  <Form.Control
                    value={form.invoice_number}
                    onChange={(e) => setForm({ ...form, invoice_number: e.target.value })}
                    className="bg-dark text-light border-secondary"
                    placeholder="Optional"
                  />
                </Form.Group>
              </Col>
              <Col md={4}>
                <Form.Group className="mb-3">
                  <Form.Label>Status</Form.Label>
                  <Form.Select
                    value={form.status}
                    onChange={(e) => setForm({ ...form, status: e.target.value })}
                    className="bg-dark text-light border-secondary"
                  >
                    <option value="draft">Draft</option>
                    <option value="open">Open</option>
                    <option value="paid">Paid</option>
                    <option value="cancelled">Cancelled</option>
                  </Form.Select>
                </Form.Group>
              </Col>
            </Row>

            <Form.Group className="mb-3">
              <Form.Label>
                {form.direction === "outgoing" ? "Client" : "Vendor"}
              </Form.Label>
              <Form.Control
                required
                value={form.counterparty}
                onChange={(e) => setForm({ ...form, counterparty: e.target.value })}
                className="bg-dark text-light border-secondary"
                placeholder="Name"
              />
            </Form.Group>

            <Form.Group className="mb-3">
              <Form.Label>Link company (optional)</Form.Label>
              <Form.Select
                value={form.company}
                onChange={(e) => setForm({ ...form, company: e.target.value })}
                className="bg-dark text-light border-secondary"
              >
                <option value="">None</option>
                {companies.map((c) => (
                  <option key={c.id} value={c.id}>
                    {c.name}
                  </option>
                ))}
              </Form.Select>
            </Form.Group>

            <Row>
              <Col md={6}>
                <Form.Group className="mb-3">
                  <Form.Label style={{ display: "block" }}>Issue date</Form.Label>
                  <DatePicker
                    selected={form.issue_date}
                    onChange={(date) => setForm({ ...form, issue_date: date })}
                    className="form-control bg-dark text-light border-secondary"
                    dateFormat="dd/MM/yyyy"
                  />
                </Form.Group>
              </Col>
              <Col md={6}>
                <Form.Group className="mb-3">
                  <Form.Label style={{ display: "block" }}>Due date</Form.Label>
                  <DatePicker
                    selected={form.due_date}
                    onChange={(date) => setForm({ ...form, due_date: date })}
                    className="form-control bg-dark text-light border-secondary"
                    dateFormat="dd/MM/yyyy"
                    isClearable
                    placeholderText="Optional"
                  />
                </Form.Group>
              </Col>
            </Row>

            <Row>
              <Col md={4}>
                <Form.Group className="mb-3">
                  <Form.Label>Net amount</Form.Label>
                  <Form.Control
                    type="number"
                    step="0.01"
                    min="0"
                    required
                    value={form.amount}
                    onChange={(e) => setForm({ ...form, amount: e.target.value })}
                    className="bg-dark text-light border-secondary"
                  />
                </Form.Group>
              </Col>
              <Col md={4}>
                <Form.Group className="mb-3">
                  <Form.Label>VAT</Form.Label>
                  <Form.Control
                    type="number"
                    step="0.01"
                    min="0"
                    value={form.vat_amount}
                    onChange={(e) => setForm({ ...form, vat_amount: e.target.value })}
                    className="bg-dark text-light border-secondary"
                  />
                </Form.Group>
              </Col>
              <Col md={4}>
                <Form.Group className="mb-3">
                  <Form.Label>Currency</Form.Label>
                  <Form.Select
                    value={form.currency}
                    onChange={(e) => setForm({ ...form, currency: e.target.value })}
                    className="bg-dark text-light border-secondary"
                  >
                    <option value="EUR">EUR</option>
                    <option value="USD">USD</option>
                    <option value="GBP">GBP</option>
                  </Form.Select>
                </Form.Group>
              </Col>
            </Row>

            <Form.Group className="mb-3">
              <Form.Label>Description</Form.Label>
              <Form.Control
                as="textarea"
                rows={2}
                value={form.description}
                onChange={(e) => setForm({ ...form, description: e.target.value })}
                className="bg-dark text-light border-secondary"
              />
            </Form.Group>

            <Form.Group className="mb-3">
              <Form.Label>
                <FaFileAlt className="me-2" />
                Invoice file (PDF, image, etc.)
              </Form.Label>
              <Form.Control
                type="file"
                accept=".pdf,.png,.jpg,.jpeg,.webp,.doc,.docx"
                onChange={(e) => {
                  setSelectedFile(e.target.files?.[0] || null);
                  setRemoveFile(false);
                }}
                className="bg-dark text-light border-secondary"
              />
              {selectedFile && (
                <Form.Text style={{ color: "#9ca3af" }}>
                  Selected: {selectedFile.name}
                </Form.Text>
              )}
              {!selectedFile && editing?.file_url && !removeFile && (
                <div className="mt-2 d-flex align-items-center gap-2">
                  <a
                    href={editing.file_url}
                    target="_blank"
                    rel="noopener noreferrer"
                    style={{ color: ACCENT }}
                  >
                    <FaDownload className="me-1" />
                    {editing.file_name || "Current file"}
                  </a>
                  <Button
                    size="sm"
                    variant="outline-danger"
                    onClick={() => setRemoveFile(true)}
                  >
                    <FaTimes className="me-1" />
                    Remove
                  </Button>
                </div>
              )}
              {removeFile && (
                <Form.Text style={{ color: "#f87171" }}>
                  File will be removed on save.
                </Form.Text>
              )}
            </Form.Group>

            <Form.Group className="mb-1">
              <Form.Label>Notes</Form.Label>
              <Form.Control
                as="textarea"
                rows={2}
                value={form.notes}
                onChange={(e) => setForm({ ...form, notes: e.target.value })}
                className="bg-dark text-light border-secondary"
              />
            </Form.Group>
          </Modal.Body>
          <Modal.Footer style={{ borderTop: "1px solid #333" }}>
            <Button variant="secondary" onClick={closeModal} disabled={saving}>
              Cancel
            </Button>
            <Button variant="primary" type="submit" disabled={saving}>
              {saving ? "Saving…" : editing ? "Update" : "Create"}
            </Button>
          </Modal.Footer>
        </Form>
      </Modal>
    </Container>
  );
};

export default Invoices;
